import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { UiNode, UiText } from '@ory/kratos-client';
import { AxiosResponse } from 'axios';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';

import { ApiFlow, SubmitResponse, BodyValues } from './common';
import Form from './Form';
import Waiting from '../Waiting';
import Login from '../Login';


/*
React Component to fetch and render a Kratos Flow.  This is intended as
a generic helper to a parent component implementing a specific flow, eg
../Login.tsx.

TLDR: Specific flows like Login, Registration, etc., can be implemented by
putting together a FlowInit and a Flow, and handing them callbacks to
make the right API calls (flowInitter/ Getter/ Submitter), etc.


Big Picture:

Many Kratos flows have an (init, get, submit) triplet of
server interactions.  FlowInit handles init, while Flow handles get/submit.
The Kratos server responses to get/submit with a UiNode tree of recommended
form nodes and current/default values, which Form and FormNode help render.

The actual API calls are cookie-cutter but have different names and 
sometimes differing options, so the actual API calls are customized per 
explicit flow and passed in through helper functions like flowGetter and 
flowSubmitter.

For now the UI is entirely auto-generated from the node tree returned by
Kratos (see UiContainer and the ui field in the response).
(eventually we may need to add a way to customize it, eg to
provide a second input for password confirmation during registration.  This
could be accomplished by adding a path to provide UI override functions
keyed to specific group and node names.)

One difficulty is that some of these UI nodes come containing default/current
values (CSRF, saved user profile values, etc) from Kratos, but
should be controlled/stored by React between flow fetches (shifting source of
truth).  Form/FormNode use the group/node name pair returned by Kratos as a
look-up key into React-side values storage and deal with some of the issues
of syncing this data and mapping it into Kratos/React appropriate values.

In particular, optional values are a pain point due to conflicting
definitions in Kratos & React:
Kratos provides undefined and accepts undefined/null for opt-out, but
treats empty string as opt-in.
However, React prefers empty string for optionals, generating warnings on
null/undefined.  It also treats undefined as an "uncontrolled value", and
this can cause undesirable UI behavior here where we intend 
"controlled values".  So, our convention is "" in React, undefined in Kratos.

*/
export default function Flow<FLOW extends ApiFlow>(
  props: FlowProps<FLOW>)
{
  const queryKey = ['kratos', 'flow', 'get', props.flowType, props.flowId];
  const query = useQuery({
    queryKey,
    queryFn: ({ queryKey: [_kratos, _flow, _get, _flowType, flowId] }) =>
      props.flowGetter(flowId)()
  });
  const [needsRefreshLogin, setNeedsRefreshLogin] = React.useState(false);

  if (query.isLoading) {
    return <Waiting message="Getting flow" />;
  }

  if (query.isError || !query.isSuccess) {
    return (
      <Alert severity="error">
        Failed to get flow!
      </Alert>);
  }

  const ui = query.data.data.ui;
  const groups = getSubmitGroups(ui.nodes);

  type AlertColor = 'error' | 'info' | 'success' | 'warning';
  const messages = ui.messages === undefined ? null : (
    <div>
    { ui.messages.map((message: UiText) =>
      <Alert 
        key={message.id}
        severity={message.type as AlertColor}
      >
        {message.text}
      </Alert>
    )}
    </div>);

  const forms = (<div>{ groups.map(group =>
    <Form
      key={group}
      submitGroup={group}
      uiNodes={getGroupNodes(ui.nodes, group)}
      flowId={props.flowId}
      flowType={props.flowType}
      flowSubmitter={props.flowSubmitter}
      loginRefresher={() => { setNeedsRefreshLogin(true); } }
      afterSuccessfulSubmit={props.afterSuccessfulSubmit} />
  )}</div>);

  if (needsRefreshLogin) {
    return <Login
      isRefreshLogin={true} />;
  }

  return (
    <Container>
    <Stack>
      { messages}
      { forms }
    </Stack>
    </Container>);
}


interface FlowProps<FLOW extends ApiFlow>
{
  flowId: string;
  flowType: string; // eg "registration", used as reactQuery cache key
  flowGetter: (flowId: string) => () => Promise<AxiosResponse<FLOW>>;
  flowSubmitter: (
    values: BodyValues, submitGroup: string, submitter: string
  ) => Promise<SubmitResponse>;

  // optional callback for eg post-login/registration redirects
  afterSuccessfulSubmit?: () => void;
}


function getSubmitGroups(nodes: UiNode[]) {
  // note: we want to guarantee preserved ordering of groups

  const ignoreGroups = ['default'];
  const seenGroups = new Set(ignoreGroups);
  const groups = nodes.map(n => n.group);
  const uniqueGroups = [];
  for (const group of groups) {
    if (seenGroups.has(group)) { continue; }
    seenGroups.add(group);
    uniqueGroups.push(group);
  }
  return uniqueGroups;
}


function shouldFilterSpecialNode(node: UiNode, group: string) {
  // here we special-case filter UiNodes that would otherwise
  // be rendered by our current model of kratos UiNode groups,
  // but that appear to be unintended or problematic.
  // (This necessity indicates our model is likely incorrect...
  // hopefully we can eventually correct the model and
  // remove this code).

  // the 'identifier' node in the default group is
  // appropriate for password login but seems to be unnecessary
  // (and gets in the way) for oidc flows...
  if (
    group === 'oidc'
    && node.group === 'default'
    && node.attributes.node_type === 'input'
    && node.attributes.name === 'identifier'
  ) {
    return true;
  }
  return false;
}

function getGroupNodes(nodes: UiNode[], group: string) {
  const foundNodes = [];
  const groups = new Set(['default', group]);
  for (const node of nodes) {
    if (groups.has(node.group)) {
      if (shouldFilterSpecialNode(node, group)) {
        continue;
      }
      foundNodes.push(node);
    }
  }
  return foundNodes;
};
